import * as React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  FormHelperText,
  Select,
} from '@mui/material';
import { z } from 'zod';
import { NumericFormat } from 'react-number-format';
import { notifyAPIClient } from '../../utils/toast';
import { apiPut } from '../../utils/apiClient';
import { useIsMobile } from '../../hooks/useResponsive';
import { Registration } from '../../types/RegistrationTable.type';
import { useTranslation } from '../../i18n';

type EditRegistrationDialogProps = {
  open: boolean;
  handleClose: () => void;
  registration: Registration | null;
  fetchRegistrations: () => void;
};

const RegistrationInfoInput = z.object({
  id: z.number(),
  sos: z
    .string({
      required_error: 'Please enter start date of service',
    })
    .date('Please enter a valid date'),
  note: z.string().optional(),
  duration: z.number().min(1, 'Duration must be at least 1').default(1),
  price: z.number().min(0, 'Price must be a positive number').default(0),
  priceForChild: z
    .number()
    .min(0, 'Price for child must be a positive number')
    .default(0),
  discountPrice: z
    .number()
    .min(0, 'Discount price must be a positive number')
    .default(0),
  amount: z.number().min(1, 'Quantity must be at least 1').default(1),
  subAmount: z
    .number()
    .min(1, 'Quantity for child amount must be at least 1')
    .default(1),
  paymentMethod: z.string(),
});
type RegistrationInfoInputType = z.infer<typeof RegistrationInfoInput>;
type FormErrors = {
  sos?: string;
  duration?: string;
  price?: string;
  priceForChild?: string;
  discountPrice?: string;
  amount?: string;
  subAmount?: string;
  paymentMethod?: string;
  note?: string;
};

const EditRegistrationDialog = ({
  open,
  handleClose,
  registration,
  fetchRegistrations,
}: EditRegistrationDialogProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [formErrors, setFormErrors] = React.useState<FormErrors>({});

  const handleConfirm = async (data: RegistrationInfoInputType) => {
    try {
      if (registration) {
        const updateRes = await apiPut<
          {
            status: number;
            message: string;
            data: Registration;
          },
          RegistrationInfoInputType
        >('/registration/update', data);
        notifyAPIClient(updateRes.message);
      }
      fetchRegistrations();
      handleClose();
    } catch (error) {
      notifyAPIClient(t('registration.unexpectedError'));
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const data = RegistrationInfoInput.parse({
        id: registration?.id,
        sos: formData.get('sos'),
        note: formData.get('note'),
        duration: Number(formData.get('duration')),
        // price: Number(formData.get('price')),
        // priceForChild: Number(formData.get('priceForChild')) || 0,
        // discountPrice: Number(formData.get('discountPrice')) || 0,
        price: Number((formData.get('price') as string).replace(/,/g, '')),
        priceForChild:
          Number(
            ((formData.get('priceForChild') as string) || '').replace(/,/g, ''),
          ) || 0,
        discountPrice:
          Number((formData.get('discountPrice') as string).replace(/,/g, '')) ||
          0,
        amount: Number(formData.get('amount')),
        subAmount: Number(formData.get('subAmount')) || 1,
        paymentMethod: formData.get('paymentMethod'),
      });

      setFormErrors({});
      handleConfirm(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormErrors;
          fieldErrors[field] = err.message;
        });
        setFormErrors(fieldErrors);
      } else {
        console.error(error);
        notifyAPIClient(
          'An unexpected error occurred while processing your request.',
        );
      }
    }
  };

  return (
    <Dialog
      fullWidth
      fullScreen={isMobile}
      open={open}
      onClose={() => {
        handleClose();
        setFormErrors({});
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Update Registration Information</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            id="sos"
            name="sos"
            className="required-field"
            label="Start Date"
            type="date"
            fullWidth
            variant="standard"
            defaultValue={
              // Date format
              registration?.sos
                ? new Date(registration.sos).toISOString().split('T')[0]
                : ''
            }
            error={Boolean(formErrors.sos)}
            helperText={formErrors.sos}
          />

          <TextField
            margin="dense"
            id="duration"
            name="duration"
            className="required-field"
            label="Duration (days)"
            type="number"
            fullWidth
            variant="standard"
            defaultValue={registration?.duration || ''}
            error={Boolean(formErrors.duration)}
            helperText={formErrors.duration}
          />

          {/* <TextField
            margin="dense"
            id="price"
            name="price"
            className="required-field"
            label="Total price for Adult"
            type="number"
            fullWidth
            variant="standard"
            defaultValue={registration?.price || ''}
            error={Boolean(formErrors.price)}
            helperText={formErrors.price}
          /> */}
          <NumericFormat
            customInput={TextField}
            thousandSeparator
            margin="dense"
            id="price"
            name="price"
            className="required-field"
            label={
              registration?.subsubtype?.serviceFor === 'GROUP'
                ? 'Total price for Adult'
                : 'Total price'
            }
            type="text"
            fullWidth
            variant="standard"
            defaultValue={registration?.price || ''}
            error={Boolean(formErrors.price)}
            helperText={formErrors.price}
          />

          <TextField
            margin="dense"
            id="amount"
            name="amount"
            className="required-field"
            label={
              registration?.subsubtype?.serviceFor === 'GROUP'
                ? 'Adult Quantity'
                : 'Quantity'
            }
            type="number"
            fullWidth
            variant="standard"
            defaultValue={registration?.amount || ''}
            error={Boolean(formErrors.amount)}
            helperText={formErrors.amount}
          />

          {registration?.subsubtype?.serviceFor === 'GROUP' && (
            <>
              {/* <TextField
                margin="dense"
                id="priceForChild"
                name="priceForChild"
                className="required-field"
                label="Total price for Child"
                type="number"
                fullWidth
                variant="standard"
                defaultValue={registration?.priceForChild || ''}
                error={Boolean(formErrors.priceForChild)}
                helperText={formErrors.priceForChild}
              /> */}
              <NumericFormat
                customInput={TextField}
                thousandSeparator
                margin="dense"
                id="priceForChild"
                name="priceForChild"
                className="required-field"
                label="Total price for Child"
                type="text"
                fullWidth
                variant="standard"
                defaultValue={registration?.priceForChild || ''}
                error={Boolean(formErrors.priceForChild)}
                helperText={formErrors.priceForChild}
              />
              <TextField
                margin="dense"
                id="subAmount"
                name="subAmount"
                className="required-field"
                label="Child Quantity"
                type="number"
                fullWidth
                variant="standard"
                defaultValue={registration?.subAmount || ''}
                error={Boolean(formErrors.subAmount)}
                helperText={formErrors.subAmount}
              />
            </>
          )}

          {/* <TextField
            margin="dense"
            id="discountPrice"
            name="discountPrice"
            label="Discount Price"
            type="number"
            fullWidth
            variant="standard"
            defaultValue={registration?.discountPrice || ''}
            error={Boolean(formErrors.discountPrice)}
            helperText={formErrors.discountPrice}
          /> */}
          <NumericFormat
            customInput={TextField}
            thousandSeparator
            margin="dense"
            id="discountPrice"
            name="discountPrice"
            label="Discount Price"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={registration?.discountPrice || ''}
            error={Boolean(formErrors.discountPrice)}
            helperText={formErrors.discountPrice}
          />

          <FormControl margin="dense" fullWidth variant="standard">
            <InputLabel id="payment-method-label" className="required-field">
              Payment Method
            </InputLabel>
            <Select
              id="payment-method"
              name="paymentMethod"
              labelId="payment-method-label"
              defaultValue={registration?.paymentMethod || ''}
              label="Payment Method"
            >
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
              <MenuItem value="DEPOSIT">Deposit</MenuItem>
            </Select>
            {formErrors.paymentMethod && (
              <FormHelperText>{formErrors.paymentMethod}</FormHelperText>
            )}
          </FormControl>

          <TextField
            margin="dense"
            id="note"
            name="note"
            label="Note"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={registration?.note || ''}
            error={Boolean(formErrors.note)}
            helperText={formErrors.note}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleClose();
              setFormErrors({});
            }}
          >
            Cancel
          </Button>
          <Button type="submit">Confirm</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditRegistrationDialog;
